import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Search as SearchIcon, X, Loader2, Sparkles, Clock, MapPin, Tag, Filter, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import ActivityCard from '../components/ActivityCard';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = 'infobel_recent_searches';

const Search = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const inputRef = useRef(null);
  
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [parsedFilters, setParsedFilters] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecentSearches(saved);
    } catch { setRecentSearches([]); }
  }, []);

  const saveRecent = (q) => {
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 8);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  };

  const removeRecent = (q) => {
    const updated = recentSearches.filter(s => s !== q);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  };

  const popularSearches = [
    { icon: Sparkles, text: t('search_trending_weekend'), type: 'trending' },
    { icon: MapPin, text: t('search_near_me'), type: 'near' },
    { icon: Tag, text: t('search_free_activities'), type: 'free' }
  ];

  const debounceTimer = useRef(null);
  const fetchSuggestions = useCallback((searchQuery) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (searchQuery.length < 2) { setSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/api/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
        setSuggestions(response.data);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    }, 300);
  }, []);

  useEffect(() => { fetchSuggestions(query); }, [query, fetchSuggestions]);

  const handleSmartSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) return;
    
    setLoading(true);
    setHasSearched(true);
    setSuggestions([]);
    setParsedFilters(null);
    saveRecent(q.trim());
    
    try {
      const response = await axios.post(`${API}/api/search/smart`, {
        query: q.trim()
      }, { withCredentials: true });
      
      setResults(response.data.activities || []);
      setParsedFilters(response.data.filters || null);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSmartSearch();
  };

  const handleQuickSearch = (type) => {
    if (type === 'trending') navigate('/discover?tab=trending');
    else if (type === 'near') navigate('/discover?tab=near_me');
    else if (type === 'free') navigate('/discover?tab=budget');
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.text);
    handleSmartSearch(suggestion.text);
  };

  const filterLabels = {
    age_groups: { icon: '👶', label: 'Ages' },
    price_range: { icon: '💰', label: 'Price' },
    indoor_outdoor: { icon: '🏠', label: 'Setting' },
    category: { icon: '🎯', label: 'Category' },
    city: { icon: '📍', label: 'Location' },
    grandparent_friendly: { icon: '👴', label: 'Grandparent friendly' },
    time_frame: { icon: '📅', label: 'When' },
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center flex-shrink-0"
              data-testid="search-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
            </button>
            
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666B67]" />
              <Input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('search_placeholder')}
                className="input-field pl-12 pr-12"
                autoFocus
                data-testid="search-input"
              />
              {query && (
                <button
                  onClick={() => { setQuery(''); setSuggestions([]); setResults([]); setHasSearched(false); setParsedFilters(null); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <X className="w-5 h-5 text-[#666B67]" />
                </button>
              )}
            </div>
            
            <button
              onClick={() => handleSmartSearch()}
              disabled={!query.trim() || loading}
              className="w-10 h-10 rounded-full bg-[#E07A5F] flex items-center justify-center flex-shrink-0 disabled:opacity-50"
              data-testid="search-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : <Sparkles className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Suggestions dropdown */}
        {suggestions.length > 0 && !hasSearched && (
          <div className="mb-6 bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="suggestions-dropdown">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#F9F8F6] text-left border-b border-[#2C302E]/5 last:border-0"
              >
                {suggestion.type === 'activity' && <SearchIcon className="w-4 h-4 text-[#E07A5F]" />}
                {suggestion.type === 'city' && <MapPin className="w-4 h-4 text-[#81B29A]" />}
                {suggestion.type === 'category' && <Tag className="w-4 h-4 text-[#666B67]" />}
                <span className="text-[#2C302E]">{suggestion.text}</span>
              </button>
            ))}
          </div>
        )}

        {/* Search results */}
        {hasSearched ? (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#E07A5F]/10 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-[#E07A5F] animate-pulse" />
                </div>
                <p className="text-[#2C302E] font-medium mb-1">{t('search_ai_thinking')}</p>
                <p className="text-sm text-[#666B67]">{t('search_ai_understanding')}</p>
              </div>
            </div>
          ) : (
            <div>
              {/* AI Parsed Filters */}
              {parsedFilters && Object.keys(parsedFilters).filter(k => k !== 'keywords').length > 0 && (
                <div className="mb-6 p-4 bg-gradient-to-r from-[#E07A5F]/5 to-[#81B29A]/5 rounded-2xl border border-[#2C302E]/5" data-testid="parsed-filters">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-[#E07A5F]" />
                    <span className="text-sm font-medium text-[#2C302E]">{t('search_ai_understood')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(parsedFilters).filter(([k]) => k !== 'keywords').map(([key, value]) => {
                      const meta = filterLabels[key];
                      if (!meta) return null;
                      const display = Array.isArray(value) ? value.join(', ') : typeof value === 'boolean' ? 'Yes' : String(value);
                      return (
                        <span key={key} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-xs font-medium text-[#2C302E] border border-[#2C302E]/10">
                          <span>{meta.icon}</span>
                          <span className="text-[#666B67]">{meta.label}:</span>
                          <span>{display}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="text-sm text-[#666B67] mb-4" data-testid="results-count">
                {results.length} {t('search_results_found')}
              </p>
              
              {results.length > 0 ? (
                <div className="grid gap-4">
                  {results.map((activity) => (
                    <ActivityCard key={activity.id} activity={activity} variant="compact" />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-[#2C302E] font-medium mb-2">{t('search_no_results')}</p>
                  <p className="text-sm text-[#666B67]">{t('search_try_different')}</p>
                </div>
              )}
            </div>
          )
        ) : (
          <>
            {/* AI Search Tip */}
            <div className="bg-gradient-to-r from-[#E07A5F]/10 to-[#81B29A]/10 rounded-2xl p-4 mb-6" data-testid="ai-search-tip">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#E07A5F] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#2C302E] mb-1">{t('search_ai_title')}</p>
                  <p className="text-sm text-[#666B67]">{t('search_ai_hint')}</p>
                </div>
              </div>
            </div>

            {/* Popular Searches */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-[#2C302E] mb-4 font-['Outfit']">{t('search_popular')}</h2>
              <div className="grid gap-2">
                {popularSearches.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickSearch(item.type)}
                    className="flex items-center gap-3 p-4 bg-white rounded-2xl hover:bg-[#F9F8F6] transition-colors text-left card-hover"
                    data-testid={`quick-search-${item.type}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#E07A5F]/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#E07A5F]" />
                    </div>
                    <span className="text-[#2C302E]">{item.text}</span>
                    <ChevronRight className="w-4 h-4 text-[#666B67] ml-auto" />
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Searches */}
            {recentSearches.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit']">{t('search_recent')}</h2>
                  <button 
                    onClick={() => { setRecentSearches([]); localStorage.removeItem(RECENT_KEY); }}
                    className="text-sm text-[#E07A5F]"
                    data-testid="clear-recent-btn"
                  >
                    {t('search_clear_all')}
                  </button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((search, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 hover:bg-white rounded-xl transition-colors group">
                      <button
                        onClick={() => { setQuery(search); handleSmartSearch(search); }}
                        className="flex items-center gap-3 flex-1 text-left"
                        data-testid={`recent-search-${index}`}
                      >
                        <Clock className="w-4 h-4 text-[#666B67]" />
                        <span className="text-[#666B67]">{search}</span>
                      </button>
                      <button
                        onClick={() => removeRecent(search)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      >
                        <X className="w-3.5 h-3.5 text-[#666B67]" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default Search;
